"use client";

import { FC } from "react";
import {
  isEditing,
  isPreviewing,
  BuilderBlock,
  Blocks,
  RegisteredComponents,
  BuilderContextInterface,
} from "@builder.io/sdk-react";
import HoverParallaxImage from "@/components/HoverParallaxImage";

export type HoverParallaxImageBlockProps = {
  foregroundImage: BuilderBlock[];
  image: BuilderBlock[];
  backgroundImage: BuilderBlock[];
  animationIcon: BuilderBlock[];
  builderBlock: BuilderBlock;
  builderComponents: RegisteredComponents;
  builderLinkComponent: unknown;
  builderContext: BuilderContextInterface;
};

export const HoverParallaxImageBlock: FC<HoverParallaxImageBlockProps> = ({
  foregroundImage,
  image,
  backgroundImage,
  animationIcon,
  builderBlock,
  builderComponents,
  builderLinkComponent,
  builderContext,
}) => {
  const getImageBlock = (blocks: BuilderBlock[], path: string) => (
    <Blocks
      blocks={blocks}
      path={path}
      parent={builderBlock?.id}
      registeredComponents={builderComponents}
      context={builderContext}
      linkComponent={builderLinkComponent}
      className="object-contain object-right"
    />
  );

  const foregroundImageBlock = getImageBlock(
    foregroundImage,
    "foregroundImage",
  );
  const imageBlock = getImageBlock(image, "image");
  const backgroundImageBlock = getImageBlock(
    backgroundImage,
    "backgroundImage",
  );
  const iconBottomAnimationBlock = getImageBlock(
    animationIcon,
    "animationIcon",
  );

  return isEditing() || isPreviewing() ? (
    <div className="border-swdc-black relative -left-[2.5rem] flex flex-col border-1 border-solid sm:w-[10rem] lg:w-[24rem]">
      <div className="flex flex-row">
        <div className="border-swdc-black w-1/2 border-1 border-solid p-2">
          Foreground Image
          {foregroundImage && foregroundImage.length > 0 ? (
            <>{foregroundImageBlock}</>
          ) : (
            <div className="bg-swdc-gray-200">{foregroundImageBlock}</div>
          )}
        </div>
        <div className="border-swdc-black relative w-1/2 border-1 border-solid p-2">
          Image
          {image && image.length > 0 ? (
            <>{imageBlock}</>
          ) : (
            <div className="bg-swdc-gray-50 relative">
              <div className="bg-swdc-gray-200 pointer-events-none absolute top-1/10 left-1/16 h-8 w-8"></div>
              <div className="h-full w-full">{imageBlock}</div>
              <div className="pointer-events-none absolute top-2 -right-2 h-full w-2 bg-[conic-gradient(#eee_25%,#ccc_0_50%,#eee_0_75%,#ccc_0)] bg-[length:16px_16px] bg-repeat"></div>
              <div className="pointer-events-none absolute -bottom-2 left-2 h-2 w-full bg-[conic-gradient(#eee_25%,#ccc_0_50%,#eee_0_75%,#ccc_0)] bg-[length:16px_16px] bg-repeat"></div>
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-row">
        <div className="border-swdc-black w-1/2 border-1 border-solid p-2">
          <div className="h-full w-full">
            Animation Icon
            {animationIcon && animationIcon.length > 0 ? (
              <>{iconBottomAnimationBlock}</>
            ) : (
              <>
                <div className="relative">
                  <span
                    className="bg-swdc-gray-300 absolute z-10 h-2 w-2 rounded-full"
                    style={{
                      animation: "hoverParallaxBallOrbit 6s linear infinite",
                    }}
                  />
                  {iconBottomAnimationBlock}
                </div>
                <style jsx>{`
                  @keyframes hoverParallaxBallOrbit {
                    0%,
                    100% {
                      top: 50%;
                      left: 25%;
                    }
                    25%,
                    75% {
                      top: 50%;
                      left: 0%;
                    }
                    50% {
                      top: 100%;
                      left: 0%;
                    }
                  }
                `}</style>
              </>
            )}
          </div>
        </div>
        <div className="border-swdc-black w-1/2 border-1 border-solid p-2">
          Background Image
          {backgroundImage && backgroundImage.length > 0 ? (
            <>{backgroundImageBlock}</>
          ) : (
            <div className="bg-[conic-gradient(#eee_25%,#ccc_0_50%,#eee_0_75%,#ccc_0)] bg-[length:16px_16px] bg-repeat">
              {backgroundImageBlock}
            </div>
          )}
        </div>
      </div>
    </div>
  ) : (
    <HoverParallaxImage
      image={imageBlock}
      backgroundImage={backgroundImageBlock}
      foregroundImage={foregroundImageBlock}
      imageBottomAnimation={iconBottomAnimationBlock}
    />
  );
};
